/**
 * workOrderDetailPayload.ts — extracted from WorkOrdersTabContent (600-line
 * cap, Phase 2 qa/quote-wo-schedule-lifecycle-2026-07-11).
 *
 * Builds the PUT payload for a work-order-detail grid row save.
 */

/**
 * BUG-2026-0011 (re-opened): mirror today's local date into the matching
 * signoff-date cell as soon as a person name is selected — no need to click
 * outside the cell. Only fills when the date is empty (so we don't clobber a
 * date the user typed manually); clearing the person clears the date cell
 * (the backend nulls the pair on save — this keeps the grid honest meanwhile).
 * Backend re-applies the same logic on save as defense-in-depth.
 */
const TEAM_TO_DATE: Record<string, string> = {
  CheckedBy: 'TestDate', ReviewedBy: 'ReviewDate', ApprovedBy: 'ApprovalDate',
};

export const mirrorSignoffDate = (
  row: Record<string, unknown>, field: string, value: unknown,
): Record<string, unknown> => {
  const dateField = TEAM_TO_DATE[field];
  if (!dateField) return row;
  if (typeof value === 'string' && value && !row[dateField]) {
    return { ...row, [dateField]: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }) };
  }
  if (!value) return { ...row, [dateField]: '' };
  return row;
};

/**
 * Date fields: `|| null` (NOT `|| undefined`). JSON.stringify drops undefined
 * keys, so a user clearing a date silently becomes a no-op send and the
 * backend keeps the old value. Null makes the clear stick.
 */
export const buildDetailPayload = (row: Record<string, unknown>): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    TestStatus: row.TestStatus as string,
    Priority: row.Priority as string,
    Instructions: row.Instructions as string,
    StartDate: (row.StartDate as string) || null,
    EndDate: (row.EndDate as string) || null,
    CheckedBy: (row.CheckedBy as string) || null,
    TestDate: (row.TestDate as string) || null,
    ReviewedBy: (row.ReviewedBy as string) || null,
    ReviewDate: (row.ReviewDate as string) || null,
    ApprovedBy: (row.ApprovedBy as string) || null,
    ApprovalDate: (row.ApprovalDate as string) || null,
    SpecimenDepth: row.SpecimenDepth != null && row.SpecimenDepth !== '' ? Number(row.SpecimenDepth) : null,
  };
  if (row.TestName) payload.TestName = row.TestName as string;
  if (row.SpecimenRef !== undefined) payload.SpecimenRef = (row.SpecimenRef as string) || null;
  return payload;
};

/**
 * BUG-2026-0190: SOFT (non-blocking) warning — true when the payload leaves the
 * same person as both Tested By (CheckedBy) and Reviewed By (ReviewedBy). The
 * submitter asked for a warning, not a hard block, so callers should still let
 * the save proceed and merely surface this to the user. Comparison is trimmed +
 * case-insensitive; empty/absent names never warn.
 */
export const isSelfReview = (payload: Record<string, unknown>): boolean => {
  const norm = (v: unknown) =>
    typeof v === 'string' ? v.trim().toLowerCase() : '';
  const tester = norm(payload.CheckedBy);
  const reviewer = norm(payload.ReviewedBy);
  return tester !== '' && tester === reviewer;
};
